import {
  CloseCircleOutlined,
  FilterOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  SlidersOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Col,
  Empty,
  Input,
  Pagination,
  Row,
  Select,
  Space,
  message,
  Popover,
  InputNumber,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useLocation, withRouter } from 'react-router-dom'
import AccessControlAction from '../../../components/AccessControlAction'
import { EmptyText } from '../../../components/Common/CellText'
import TableComponent from '../../../components/Common/TableComponent'
import PageTitle from '../../../components/PageTitle'
import AppPageHeader from '../../../components/AppPageHeader'
import UnreadTextOutGoing from '../../../components/UnreadText/UnreadTextOutGoing'
import { systemName } from '../../../config'
import {
  ACL_ACTION_TYPE,
  DATE_FORMAT_DEFAULT,
  DOCUMENT_TYPE,
} from '../../../constants'
import DocumentsLayout from '../../../layouts/DocumentsLayout'
import utils from '../../../utils'
import { FilterButtonWrapper } from '../InternalDocumentIncomingDocPage/InternalDocumentIncomingDocPageStyled'
import AdvanceFilterBlock from './AdvanceFilterBlock'
import {
  SearchBar,
  TableFooterActionWrapper,
} from './InternalDocumentOutgoingDocPageStyled'

const { Search } = Input
const { Option } = Select

const InternalDocumentOutgoingOutgoingDocPage = props => {
  const {
    history,
    internalDocumentStore,
    loadingAnimationStore,
    bookStore,
    authorityStore,
    sameDocumentStore,
  } = props

  const {
    internalDocumentOutgoingList,
    internalDocumentOutgoingListTotalCount,
    queryParamsOutgoing,
    setQueryParamsOutgoing,
  } = internalDocumentStore

  const location = useLocation()
  const queryStringParse = queryString.parse(location.search)

  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [countFilter, setCountFilter] = useState(0)
  const [isRefreshPage, setIsRefreshPage] = useState(true)
  const [searchValue, setSearchValue] = useState(
    queryParamsOutgoing.title || ''
  )

  useEffect(() => {
    const pageFromState =
      history.location.state?.page || queryStringParse.page || 0
    const sizeFromState =
      history.location.state?.size || queryStringParse.size || 10
    const onlyUnreadFromState =
      history.location.state?.onlyUnread ||
      queryStringParse.only_unread === 'true' ||
      false

    const isFreshLoad = !history.location.state
    const resetParams = {
      title: isFreshLoad ? null : queryStringParse.title || null,
      book_group_id: isFreshLoad
        ? null
        : queryStringParse.book_group_id || null,
      book_id: isFreshLoad ? null : queryStringParse.book_id || null,
      from_date_issued: isFreshLoad
        ? null
        : queryStringParse.from_date_issued || null,
      to_date_issued: isFreshLoad
        ? null
        : queryStringParse.to_date_issued || null,
      document_type: isFreshLoad
        ? null
        : queryStringParse.document_type || null,
      urgency_level: isFreshLoad
        ? null
        : queryStringParse.urgency_level || null,
      authority_name: isFreshLoad
        ? null
        : queryStringParse.authority_name || null,
      signer: isFreshLoad ? null : queryStringParse.signer || null,
      is_sign: isFreshLoad
        ? null
        : queryStringParse.is_sign === 'true'
        ? true
        : queryStringParse.is_sign === 'false'
        ? false
        : null,
      only_unread: onlyUnreadFromState,
      page: parseInt(pageFromState),
      size: parseInt(sizeFromState),
    }

    setQueryParamsOutgoing(resetParams)

    if (isFreshLoad) {
      history.replace('/internal-document/outgoing-document', {
        page: 0,
        size: 10,
        onlyUnread: false,
      })
    }

    const fetchData = async () => {
      try {
        loadingAnimationStore.setTableLoading(true)
        authorityStore.setAuthorityIssuedsQueryParams({
          type: DOCUMENT_TYPE.OUTGOING,
          key: null,
        })
        await Promise.all([
          authorityStore.getAuthorityIssueds(),
          bookStore.getBookGroup('DI'),
          internalDocumentStore.getInternalDocumentOutgoingList(),
        ])
        setIsRefreshPage(false)
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    }

    fetchData()

    return () => {
      internalDocumentStore.clearInternalDocumentOutgoingList()
      bookStore.clearBookGroupList()
    }
  }, [])

  const NGAY_DI_WIDTH = 100
  const SO_HIEU_WIDTH = 100
  const VBKS_WIDTH = 200
  const TRICH_YEU_WIDTH = 400
  const BO_PHAN_DU_THAO_WIDTH = 250
  const NGUOI_KY_WIDTH = 150
  const SCROLL_WIDTH = 1000

  const tableColumns = [
    {
      title: 'Ngày đi',
      width: NGAY_DI_WIDTH,
      render: record =>
        record.date_issued != null ? (
          <UnreadTextOutGoing
            text={moment(record.date_issued).format(DATE_FORMAT_DEFAULT)}
            isUnread={checkUnread(record)}
            width={NGAY_DI_WIDTH}
          />
        ) : (
          <EmptyText>Không có.</EmptyText>
        ),
    },
    {
      title: 'Số hiệu',
      width: SO_HIEU_WIDTH,
      render: record => (
        <UnreadTextOutGoing
          text={record.document_number}
          isUnread={checkUnread(record)}
          width={SO_HIEU_WIDTH}
        />
      ),
    },
    {
      title: 'Trích yếu',
      width: TRICH_YEU_WIDTH,
      render: record => (
        <UnreadTextOutGoing
          text={record.title}
          isUnread={checkUnread(record)}
          width={TRICH_YEU_WIDTH}
        />
      ),
    },
    {
      title: 'Văn bản ký số',
      width: VBKS_WIDTH,
      render: record => (
        <UnreadTextOutGoing
          text={record.is_sign ? 'Đã ký số' : ''}
          isUnread={checkUnread(record)}
          width={VBKS_WIDTH}
        />
      ),
    },
    {
      title: 'Bộ phận dự thảo',
      width: BO_PHAN_DU_THAO_WIDTH,
      render: record => (
        <UnreadTextOutGoing
          text={record.authority_issued}
          isUnread={checkUnread(record)}
          width={BO_PHAN_DU_THAO_WIDTH}
        />
      ),
    },
    {
      title: 'Người ký',
      width: NGUOI_KY_WIDTH,
      render: record =>
        record.signer ? (
          <UnreadTextOutGoing
            text={record.signer}
            isUnread={checkUnread(record)}
            width={NGUOI_KY_WIDTH}
          />
        ) : (
          <EmptyText>Không có.</EmptyText>
        ),
    },
  ]

  const checkUnread = record => record.status_doc_outgoing === 'UNREAD'

  const exportDocument = useCallback(async () => {
    const col = [
      'Ngày đi',
      'Số hiệu',
      'Trích yếu',
      'Bộ phận dự thảo',
      'Người ký',
    ]
    let rows = [col]

    try {
      setExportLoading(true)
      const res = await internalDocumentStore.getForExportInternalDocumentOutgoingListAll()
      if (res && res.data && res.data.data) {
        res.data.data.forEach(item => {
          rows.push([
            moment(item.date_issued).format(DATE_FORMAT_DEFAULT),
            item.document_number,
            item.title,
            item.authority_issued,
            item.signer || 'Không có.',
          ])
        })
      }
      utils.exportExcel(
        rows,
        'VBNB - văn bản đi - ' + moment().format('YYYY-MM-DD hmms')
      )
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setExportLoading(false)
    }
  }, [])

  const handleChangeParams = params => {
    const isFilterChange = Object.keys(params).some(
      key => !['page', 'size'].includes(key)
    )
    const updatedParams = {
      ...queryParamsOutgoing,
      page: isFilterChange
        ? 0
        : params.page !== undefined
        ? params.page
        : queryParamsOutgoing.page || 0,
      ...params,
    }
    setQueryParamsOutgoing(updatedParams)
    const queryStringStringify = queryString.stringify(updatedParams, {
      skipNull: true,
    })
    history.replace(
      `/internal-document/outgoing-document?${queryStringStringify}`,
      {
        ...history.location.state,
        page: updatedParams.page,
        size: updatedParams.size || 10,
        onlyUnread: updatedParams.only_unread,
      }
    )
  }

  const handleQuickSearch = title => {
    handleChangeParams({ title: title ? title.trim() : null })
  }

  const handleChangeUnread = event => {
    const only_unread = event?.target?.checked
    handleChangeParams({ only_unread })
  }

  const onChangePagination = pageIndex => {
    handleChangeParams({ page: pageIndex - 1 })
  }

  const getInternalDocumentOutgoingList = async () => {
    try {
      loadingAnimationStore.setTableLoading(true)
      await internalDocumentStore.getInternalDocumentOutgoingList()
    } catch (error) {
      console.log(error)
      message.error(error?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  useEffect(() => {
    if (isRefreshPage) return
    getInternalDocumentOutgoingList()
  }, [queryParamsOutgoing])

  useEffect(() => {
    let count = 0
    if (queryParamsOutgoing.title) count++
    if (
      queryParamsOutgoing.from_date_issued &&
      queryParamsOutgoing.to_date_issued
    )
      count++
    if (queryParamsOutgoing.document_type) count++
    if (queryParamsOutgoing.urgency_level) count++
    if (queryParamsOutgoing.signer) count++
    if (queryParamsOutgoing.book_group_id) count++
    if (queryParamsOutgoing.book_id) count++
    if (queryParamsOutgoing.authority_name) count++
    if (queryParamsOutgoing.is_sign !== null) count++
    setCountFilter(count)
  }, [queryParamsOutgoing])

  useEffect(() => {
    setSearchValue(queryParamsOutgoing.title || '')
  }, [queryParamsOutgoing.title])

  return (
    <DocumentsLayout
      title={
        <AppPageHeader
          title="Danh sách văn bản"
          addon={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Search
                style={{
                  width: 300,
                }}
                size="large"
                allowClear
                onSearch={value => handleQuickSearch(value)}
                defaultValue={queryStringParse.title}
                maxLength={500}
                placeholder={'Tìm kiếm văn bản theo Trích yếu / Số hiệu'}
                className="custom-search-input fixed-width"
              />
              <FilterButtonWrapper style={{ display: 'inline-block' }}>
                <span
                  className={'count-filter'}
                  style={{ display: !countFilter && 'none' }}>
                  {countFilter}
                </span>
                <Popover
                  content={
                    <AdvanceFilterBlock
                      handleChangeParams={handleChangeParams}
                      onClose={() => setShowAdvanceFilter(false)}
                    />
                  }
                  trigger="click"
                  visible={showAdvanceFilter}
                  onVisibleChange={visible => setShowAdvanceFilter(visible)}
                  placement="bottomRight"
                  overlayClassName="custom-page-filter-dropdown-container">
                  <Button
                    size="large"
                    icon={<SlidersOutlined />}
                  />
                </Popover>
              </FilterButtonWrapper>

              <Button
                size="large"
                loading={exportLoading}
                onClick={exportDocument}
                icon={<PrinterOutlined />}>
                Xuất dữ liệu
              </Button>

              <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__OUTGOING}>
                <Button
                  size="large"
                  icon={<PlusCircleOutlined />}
                  onClick={() => {
                    sameDocumentStore.setDataSameDocument(undefined)
                    history.push('/internal-document/outgoing-document/create')
                  }}
                  type={'primary'}>
                  Tạo mới văn bản đi
                </Button>
              </AccessControlAction>
            </div>
          }
        />
      }
      selectedKey="outgoing-document"
      linkToIncomingDocument="/internal-document/incoming-document"
      linkToOutgoingDocument="/internal-document/outgoing-document"
      linkToSignDocument="/internal-document/sign-document"
      linkToConfigDocument="/internal-document/config-document/document-books">
      <Helmet>
        <title>
          Văn bản đi | {systemName} Quản lý VB {systemName}
        </title>
      </Helmet>

      <div>
        <TableComponent
          onRow={record => ({
            onClick: () => {
              const currentPage = queryParamsOutgoing.page || 0
              history.push(
                `/internal-document/outgoing-document/view/${record.code}`,
                {
                  from: '/internal-document/outgoing-document',
                  page: currentPage,
                  size: queryParamsOutgoing.size || 10,
                  onlyUnread: queryParamsOutgoing.only_unread,
                }
              )
            },
          })}
          rowKey={record => record.id}
          dataSource={internalDocumentOutgoingList}
          columns={tableColumns}
          pagination={false}
          scroll={{ x: SCROLL_WIDTH }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={'Không có dữ liệu'}
              />
            ),
          }}
          loading={{
            tip: 'Đang tải văn bản...',
            spinning: loadingAnimationStore.tableLoading,
          }}
        />
        <TableFooterActionWrapper>
          <div className="customPaginationWrapper">
            <div className="left-content-box">
              <span>Hiển thị</span>
              <Select
                value={queryParamsOutgoing.size || 10}
                onChange={value => handleChangeParams({ size: value, page: 0 })}>
                <Select.Option value={10}>10 / trang</Select.Option>
                <Select.Option value={20}>20 / trang</Select.Option>
                <Select.Option value={50}>50 / trang</Select.Option>
                <Select.Option value={100}>100 / trang</Select.Option>
              </Select>
              <span>Đi đến</span>
              <InputNumber
                min={1}
                max={Math.ceil(
                  internalDocumentOutgoingListTotalCount /
                    (queryParamsOutgoing.size || 10)
                )}
                value={(queryParamsOutgoing.page || 0) + 1}
                onPressEnter={e => {
                  const value = e.target.value
                  if (value) {
                    onChangePagination(
                      Number(value),
                      queryParamsOutgoing.size || 10
                    )
                  }
                }}
                onBlur={e => {
                  const value = e.target.value
                  if (value) {
                    onChangePagination(
                      Number(value),
                      queryParamsOutgoing.size || 10
                    )
                  }
                }}
              />
            </div>
            <Pagination
              onChange={onChangePagination}
              hideOnSinglePage={true}
              total={internalDocumentOutgoingListTotalCount}
              pageSize={queryParamsOutgoing.size || 10}
              current={(queryParamsOutgoing.page || 0) + 1}
              showSizeChanger={false}
              showLessItems
              className="customPagination"
            />
          </div>
        </TableFooterActionWrapper>
      </div>

    </DocumentsLayout>
  )
}

InternalDocumentOutgoingOutgoingDocPage.propTypes = {}

export default memo(
  withRouter(
    inject(
      'internalDocumentStore',
      'loadingAnimationStore',
      'authorityStore',
      'bookStore',
      'selectUserStore',
      'sameDocumentStore'
    )(observer(InternalDocumentOutgoingOutgoingDocPage))
  )
)
